
const libPictProvider = require('pict-provider');
const libUserSearch = require('../access/User-Search.js');

/**
 * GroupComposition — the Composition + Members tabs of the Access workspace.
 *
 * A Permission Group is a Role with Type='PermissionGroup'; a Role composes groups (and other roles) via a
 * RoleEdge (Parent=this role, Child=the included group). The resolver inherits a parent's children's
 * permissions (api/permissions includeChildRoles). This provider:
 *   - Composition tab: lists the selected role/group's direct children (what it INCLUDES) + an add-child
 *     picker; create/remove a child = POST/DELETE a RoleEdge (with client-side dedup — the schema has no
 *     unique constraint).
 *   - Members tab: lists the USERS who hold the selected role/group — read-only. It resolves membership the
 *     same way the Home app does, via the Permissions "Users With Role" endpoint
 *     (Permissions/Users/With/Role/<id>/At/<Context>/Level), which folds in parent roles (anyone who inherits
 *     this role through a role that composes it) and does NOT cascade contexts. We query it once at the
 *     Customer level and once at the Project level, then tag each member with whether they hold the role at
 *     the User (customer-wide, UserRoleJoin) level or the Project (ProjectUserJoin / UserContextRole) level.
 *
 * Subject + customer come from the AccessWorkspace provider; this owns AppData.AccessWorkspace.Composition
 * (RoleEdges for the Composition tab) and AppData.AccessWorkspace.Membership (users for the Members tab).
 */
class GroupCompositionProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get workspace()
	{
		return this.pict.providers.AccessWorkspace;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get composition()
	{
		const tmpWorkspaceState = this.workspace.state;
		if (!tmpWorkspaceState.Composition)
		{
			tmpWorkspaceState.Composition =
			{
				SubjectID: 0, Loading: false, Loaded: false, Error: '', Saving: false, AddPickerValue: '',
				Children: [], Candidates: [],
				LoadingSlot: [], ErrorSlot: [], ChildrenSlot: [], ChildEmptySlot: [],
				AddSlot: [], CandidateOptions: [],
			};
		}
		return tmpWorkspaceState.Composition;
	}

	get membership()
	{
		const tmpWorkspaceState = this.workspace.state;
		if (!tmpWorkspaceState.Membership)
		{
			tmpWorkspaceState.Membership =
			{
				SubjectID: 0, Loading: false, Loaded: false, Error: '',
				Members: [], CountLabel: '',
				LoadingSlot: [], ErrorSlot: [], MembersSlot: [], MemberEmptySlot: [],
				// Bulk "add users to this role": a multi-select user picker writes ids here, then addMembersAsync
				// PUTs each assignment at the workspace's current context.
				AddUserIDs: [], AddSaving: false, AddError: '', AddNotice: '',
				AddSlot: [], AddErrorSlot: [], AddNoticeSlot: [], AddSavingSlot: [],
			};
		}
		return tmpWorkspaceState.Membership;
	}

	// --- REST ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_postJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_putJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.putJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_deleteJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			// The rest client exposes deleteEntity(entity, id) (there is no raw deleteJSON).
			const tmpMatch = /^([A-Za-z][A-Za-z0-9]*)\/(\d+)$/.exec(String(pURL));
			if (tmpMatch)
			{
				this.restClient.deleteEntity(tmpMatch[1], tmpMatch[2], (pError, pBody) => pError ? reject(pError) : resolve(pBody));
				return;
			}
			const tmpBase = this.restClient.serverURL || '/1.0/';
			fetch(`${tmpBase}${pURL}`, { method: 'DELETE', credentials: 'include' })
				.then((pResponse) =>
				{
					if (!pResponse.ok) { reject(new Error(`HTTP ${pResponse.status}`)); return; }
					pResponse.text().then((pText) => resolve(pText ? JSON.parse(pText) : {})).catch(() => resolve({}));
				})
				.catch(reject);
		});
	}

	async _getFilteredAll(pEntitySet, pFilter, pCap)
	{
		const tmpCap = pCap || 2000;
		let tmpTotal = tmpCap;
		try
		{
			const tmpCount = await this._getJSON(`${pEntitySet}/Count/FilteredTo/${pFilter}`);
			tmpTotal = Math.min((tmpCount && tmpCount.Count) || 0, tmpCap);
		}
		catch (pError)
		{
			tmpTotal = tmpCap;
		}
		if (!tmpTotal)
		{
			return [];
		}
		const tmpRows = await this._getJSON(`${pEntitySet}/FilteredTo/${pFilter}/0/${tmpTotal}`);
		return Array.isArray(tmpRows) ? tmpRows : [];
	}

	// --- Load ---

	/**
	 * Load the child RoleEdges (what this role/group INCLUDES) for the workspace's current role/group. No-op
	 * for a user subject.
	 * @param {Boolean} [pForce]
	 */
	async loadAsync(pForce)
	{
		const tmpWorkspaceState = this.workspace.state;
		if (!tmpWorkspaceState.SubjectRecord || tmpWorkspaceState.SubjectKind === 'user')
		{
			return;
		}
		const tmpID = Number(tmpWorkspaceState.SubjectID);
		const tmpComposition = this.composition;
		if (tmpComposition.Loaded && !pForce && tmpComposition.SubjectID === tmpID)
		{
			this._render();
			return;
		}
		tmpComposition.SubjectID = tmpID;
		tmpComposition.Loaded = false;
		tmpComposition.Error = '';
		tmpComposition.Loading = true;
		this._render();
		try
		{
			const tmpChildEdges = await this._getFilteredAll('RoleEdges', `FBV~ParentIDRole~EQ~${tmpID}~FBV~Deleted~EQ~0`);
			const tmpNameByID = this._roleNameMap();
			tmpComposition.Children = tmpChildEdges.map((pEdge) => this._edgeRow(pEdge, pEdge.ChildIDRole, tmpNameByID));
			tmpComposition.Loaded = true;
		}
		catch (pError)
		{
			tmpComposition.Error = `Could not load composition: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpComposition.Loading = false;
		this._render();
	}

	/**
	 * Load the USERS who hold the workspace's current role/group (the Members tab). No-op for a user subject.
	 *
	 * Mirrors the Home app: the Permissions "Users With Role" endpoint resolves membership WITH parent roles
	 * folded in (so anyone who inherits this role through a composing role is included) but does NOT cascade
	 * contexts. We ask it twice — once at the Customer level (customer-wide UserRoleJoin members) and once at
	 * the Project level (ProjectUserJoin + UserContextRole members across every project) — and merge the two,
	 * tagging each member with the level(s) at which they hold the role.
	 * @param {Boolean} [pForce]
	 */
	async loadMembersAsync(pForce)
	{
		const tmpWorkspaceState = this.workspace.state;
		if (!tmpWorkspaceState.SubjectRecord || tmpWorkspaceState.SubjectKind === 'user')
		{
			return;
		}
		const tmpID = Number(tmpWorkspaceState.SubjectID);
		const tmpMembership = this.membership;
		if (tmpMembership.Loaded && !pForce && tmpMembership.SubjectID === tmpID)
		{
			this._render();
			return;
		}
		tmpMembership.SubjectID = tmpID;
		tmpMembership.Loaded = false;
		tmpMembership.Error = '';
		tmpMembership.Loading = true;
		this._render();
		try
		{
			const tmpResults = await Promise.all(
				[
					this._getJSON(`Permissions/Users/With/Role/${tmpID}/At/Customer/Level`),
					this._getJSON(`Permissions/Users/With/Role/${tmpID}/At/Project/Level`),
				]);
			tmpMembership.Members = this._buildMembers(tmpResults[0], tmpResults[1], tmpID);
			tmpMembership.Loaded = true;
		}
		catch (pError)
		{
			tmpMembership.Error = `Could not load members: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpMembership.Loading = false;
		this._render();
	}

	/**
	 * Merge the customer-level + project-level "users with role" responses into one de-duplicated member list.
	 * Each user is keyed by IDUser and tagged AtUserLevel / AtProjectLevel; the project-level entry carries the
	 * count of distinct projects (from the endpoint's per-user ContextRoles[]). A member reached only through a
	 * composing role (FromRoles that is not the subject role) is annotated so the inheritance is visible.
	 * @param {Array} pUserLevelRows - response from .../At/Customer/Level
	 * @param {Array} pProjectLevelRows - response from .../At/Project/Level
	 * @param {Number} pSubjectID - the role/group being viewed
	 * @return {Array}
	 */
	_buildMembers(pUserLevelRows, pProjectLevelRows, pSubjectID)
	{
		const tmpByID = {};
		const tmpOrder = [];
		const fEnsure = (pUser) =>
		{
			if (!tmpByID[pUser.IDUser])
			{
				tmpByID[pUser.IDUser] =
				{
					IDUser: pUser.IDUser,
					Label: libUserSearch.userLabel(pUser),
					Sub: pUser.LoginID || pUser.Email || '',
					AtUserLevel: false, AtProjectLevel: false, ProjectCount: 0,
					// Direct = holds a UserRoleJoin/UserContextRole of its own for THIS role (removable here);
					// vs inherited-only through a composing role (removed on the composing role, not here).
					DirectAtUser: false, DirectAtProject: false, ProjectContextIDs: [],
					ViaNames: {},
				};
				tmpOrder.push(pUser.IDUser);
			}
			return tmpByID[pUser.IDUser];
		};
		(Array.isArray(pUserLevelRows) ? pUserLevelRows : []).forEach((pUser) =>
		{
			if (!pUser || !pUser.IDUser)
			{
				return;
			}
			const tmpMember = fEnsure(pUser);
			tmpMember.AtUserLevel = true;
			if (this._isDirectHolder(pUser, pSubjectID)) { tmpMember.DirectAtUser = true; }
			this._collectVia(tmpMember, pUser, pSubjectID);
		});
		(Array.isArray(pProjectLevelRows) ? pProjectLevelRows : []).forEach((pUser) =>
		{
			if (!pUser || !pUser.IDUser)
			{
				return;
			}
			const tmpMember = fEnsure(pUser);
			tmpMember.AtProjectLevel = true;
			tmpMember.ProjectCount = Array.isArray(pUser.ContextRoles) ? pUser.ContextRoles.length : tmpMember.ProjectCount;
			// The per-project IDContexts (ContextRoles[]) — retained so a project-level membership can be
			// removed precisely (the customer-level path only needs idCustomer).
			if (this._isDirectHolder(pUser, pSubjectID))
			{
				tmpMember.DirectAtProject = true;
				tmpMember.ProjectContextIDs = (Array.isArray(pUser.ContextRoles) ? pUser.ContextRoles : []).map(Number).filter((pID) => pID > 0);
			}
			this._collectVia(tmpMember, pUser, pSubjectID);
		});
		return tmpOrder
			.map((pID) => tmpByID[pID])
			.map((pMember) => Object.assign(pMember, { ViaLabel: Object.keys(pMember.ViaNames).join(', ') }))
			.sort((pA, pB) => String(pA.Label).localeCompare(String(pB.Label)));
	}

	/**
	 * Record the names of any roles (other than the subject) that grant a user this membership — i.e. the
	 * composing roles they inherit it through, from the endpoint's per-user FromRoles[].
	 * @param {Object} pMember
	 * @param {Object} pUser
	 * @param {Number} pSubjectID
	 */
	_collectVia(pMember, pUser, pSubjectID)
	{
		(Array.isArray(pUser.FromRoles) ? pUser.FromRoles : []).forEach((pRole) =>
		{
			if (pRole && pRole.IDRole && Number(pRole.IDRole) !== Number(pSubjectID))
			{
				pMember.ViaNames[pRole.Name || `Role ${pRole.IDRole}`] = true;
			}
		});
	}

	/**
	 * Does this user hold the subject role DIRECTLY (a UserRoleJoin/UserContextRole of its own) rather than
	 * only inheriting it through a composing role? The endpoint's FromRoles[] lists the roles that grant the
	 * membership; a direct holder has the subject role itself in that list (or, defensively, no FromRoles info
	 * at all). Only a direct membership can be removed here — an inherited one lives on its composing role.
	 * @param {Object} pUser @param {Number} pSubjectID
	 * @return {Boolean}
	 */
	_isDirectHolder(pUser, pSubjectID)
	{
		const tmpFromRoles = Array.isArray(pUser.FromRoles) ? pUser.FromRoles : [];
		if (tmpFromRoles.length === 0) { return true; }
		return tmpFromRoles.some((pRole) => pRole && Number(pRole.IDRole) === Number(pSubjectID));
	}


	_projectCountLabel(pCount)
	{
		const tmpCount = Number(pCount) || 0;
		if (tmpCount <= 0)
		{
			return 'Project level';
		}
		return `Project level · ${tmpCount} project${tmpCount === 1 ? '' : 's'}`;
	}

	_roleNameMap()
	{
		const tmpMap = {};
		(this.workspace.rolesAndGroups || []).forEach((pRole) =>
		{
			tmpMap[pRole.IDRole] = pRole;
		});
		return tmpMap;
	}

	_edgeRow(pEdge, pOtherID, pNameByID)
	{
		const tmpRole = pNameByID[pOtherID] || {};
		return {
			IDRoleEdge: pEdge.IDRoleEdge,
			IDRole: pOtherID,
			Label: tmpRole.Name || `Role ${pOtherID}`,
			KindLabel: (tmpRole.Type === 'PermissionGroup') ? 'Permission Group' : 'Role',
			Icon: (tmpRole.Type === 'PermissionGroup') ? 'fas fa-layer-group' : 'fas fa-user-shield',
			Kind: (tmpRole.Type === 'PermissionGroup') ? 'group' : 'role',
		};
	}

	// --- Mutations ---

	setAddPicker(pValue)
	{
		this.composition.AddPickerValue = pValue || '';
	}

	/**
	 * Compose a child role/group into the selected role via a new RoleEdge (deduped against existing children).
	 * @param {Number} [pChildID] - defaults to the add-picker selection
	 */
	async addChildAsync(pChildID)
	{
		const tmpComposition = this.composition;
		const tmpChildID = Number(pChildID || tmpComposition.AddPickerValue);
		const tmpParentID = tmpComposition.SubjectID;
		if (!(tmpChildID > 0) || tmpComposition.Saving)
		{
			return;
		}
		if (tmpChildID === tmpParentID)
		{
			tmpComposition.Error = 'A role cannot include itself.';
			this._render();
			return;
		}
		if (tmpComposition.Children.some((pChild) => Number(pChild.IDRole) === tmpChildID))
		{
			tmpComposition.Error = 'That group is already included.';
			this._render();
			return;
		}
		tmpComposition.Saving = true;
		tmpComposition.Error = '';
		this._render();
		try
		{
			await this._postJSON('RoleEdge', { ParentIDRole: tmpParentID, ChildIDRole: tmpChildID, IDCustomer: this.workspace.idCustomer });
			tmpComposition.AddPickerValue = '';
			await this.loadAsync(true);
		}
		catch (pError)
		{
			tmpComposition.Error = `Could not add: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpComposition.Saving = false;
		this._render();
	}

	/**
	 * Remove a composed child by deleting its RoleEdge.
	 * @param {Number} pIDRoleEdge
	 */
	async removeChildAsync(pIDRoleEdge)
	{
		const tmpComposition = this.composition;
		if (tmpComposition.Saving)
		{
			return;
		}
		tmpComposition.Saving = true;
		tmpComposition.Error = '';
		this._render();
		try
		{
			await this._deleteJSON(`RoleEdge/${pIDRoleEdge}`);
			await this.loadAsync(true);
		}
		catch (pError)
		{
			tmpComposition.Error = `Could not remove: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpComposition.Saving = false;
		this._render();
	}

	selectRole(pID)
	{
		if (Number(pID) > 0)
		{
			this.workspace.selectSubject('role', pID);
		}
	}

	// --- Add members (bulk-assign users to the role/group) ---

	/**
	 * (Re)mount the multi-select user picker into #acw-add-members-picker (Members tab). Server-side user search,
	 * customer-scoped; the selected ids write to Membership.AddUserIDs, and addMembersAsync assigns them all. Called
	 * from the AccessWorkspace view's onAfterRender (no-ops unless the mount is present = editable Members tab).
	 */
	mountAddMembersPicker()
	{
		const tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement('#acw-add-members-picker').length === 0) { return; }
		const tmpView = tmpPicker.createPicker('AccessWorkspace-AddMembers',
			{
				DestinationAddress: '#acw-add-members-picker',
				ValueAddress: 'AppData.AccessWorkspace.Membership.AddUserIDs',
				Mode: 'multi',
				Searchable: true,
				Placeholder: 'Search users by name, login ID or email…',
				DataProvider: this._memberUserDataProvider(),
				ResolveValue: this._memberUserResolveValue(),
			});
		if (tmpView && (typeof tmpView.render === 'function')) { tmpView.render(); }
	}

	/** Server-side user search (customer-scoped) for the add-members picker. Empty term → browse the customer's users. */
	_memberUserDataProvider()
	{
		return libUserSearch.userPickerDataProvider((pURL) => this._getJSON(pURL), { Scope: () => `FBV~IDCustomer~EQ~${this.workspace.idCustomer}~FBV~Deleted~EQ~0`, PageSize: 25 });
	}

	/** Resolve a selected user id → {Value,Text} so its chip shows a name. */
	_memberUserResolveValue()
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue);
			if (!tmpID) { return Promise.resolve(null); }
			return this._getJSON(`User/${tmpID}`)
				.then((pUser) => ((pUser && pUser.IDUser) ? libUserSearch.userPickerOption(pUser) : { Value: tmpID, Text: `#${tmpID}` }))
				.catch(() => ({ Value: tmpID, Text: `#${tmpID}` }));
		};
	}

	/**
	 * Assign every picked user to the selected role/group at the workspace's current context — the bulk path that
	 * replaces visiting each user's Roles & Groups tab. Same grant endpoint the User subject uses:
	 * PUT Permissions/Role/For/User (Customer → UserRoleJoin, Project/Lab → UserContextRole). Bounded concurrency.
	 */
	async addMembersAsync()
	{
		const tmpMembership = this.membership;
		const tmpWorkspaceState = this.workspace.state;
		const tmpIDRole = Number(tmpWorkspaceState.SubjectID);
		const tmpUserIDs = (tmpMembership.AddUserIDs || []).map(Number).filter((pID) => pID > 0);
		if (!(tmpIDRole > 0) || tmpMembership.AddSaving) { return; }
		if (!tmpUserIDs.length)
		{
			tmpMembership.AddError = 'Search for and pick one or more users to add first.';
			tmpMembership.AddNotice = '';
			this._render();
			return;
		}
		const tmpContext = tmpWorkspaceState.Context || 'Customer';
		const tmpIDContext = (tmpContext === 'Customer') ? this.workspace.idCustomer : Number(tmpWorkspaceState.IDContext);
		if (!(tmpIDContext > 0))
		{
			tmpMembership.AddError = `Enter a ${tmpContext} ID in the context bar first.`;
			tmpMembership.AddNotice = '';
			this._render();
			return;
		}
		tmpMembership.AddSaving = true;
		tmpMembership.AddError = '';
		tmpMembership.AddNotice = '';
		this._render();
		let tmpOK = 0;
		let tmpFail = 0;
		const tmpConcurrency = 6;
		for (let tmpStart = 0; tmpStart < tmpUserIDs.length; tmpStart += tmpConcurrency)
		{
			const tmpBatch = tmpUserIDs.slice(tmpStart, tmpStart + tmpConcurrency);
			/* eslint-disable no-await-in-loop */
			await Promise.all(tmpBatch.map((pIDUser) =>
				this._putJSON('Permissions/Role/For/User', { IDRole: tmpIDRole, IDUser: pIDUser, Context: tmpContext, IDContext: tmpIDContext })
					.then(() => { tmpOK++; }, () => { tmpFail++; })));
			/* eslint-enable no-await-in-loop */
		}
		tmpMembership.AddSaving = false;
		tmpMembership.AddUserIDs = [];   // clear the picker selection (re-mount reads this empty)
		tmpMembership.AddNotice = tmpFail
			? `Added ${tmpOK} user${tmpOK === 1 ? '' : 's'}; ${tmpFail} could not be added (you may lack permission in this context).`
			: `Added ${tmpOK} user${tmpOK === 1 ? '' : 's'} to this ${(tmpWorkspaceState.SubjectKind === 'group') ? 'group' : 'role'}.`;
		await this.loadMembersAsync(true);   // reload the members list (re-renders)
	}

	/**
	 * Remove a user's DIRECT membership of the selected role/group — the inverse of addMembersAsync, so the
	 * Members tab both grants and revokes (previously you could only remove from the User subject's tab). Deletes
	 * the customer-wide UserRoleJoin and/or each project UserContextRole the user directly holds for this role,
	 * via the same endpoint the User subject uses: DELETE Permissions/Role/{IDRole}/For/User/{IDUser}/In/{Context}/{IDContext}.
	 * Inherited-only (via a composing role) members are never offered a remove here, so there is nothing dangling to delete.
	 * @param {Number} pIDUser
	 */
	async removeMemberAsync(pIDUser)
	{
		const tmpMembership = this.membership;
		const tmpWorkspaceState = this.workspace.state;
		const tmpIDRole = Number(tmpWorkspaceState.SubjectID);
		if (!this.workspace.isRoleManager || !(tmpIDRole > 0) || tmpMembership.RemovingID) { return; }
		const tmpMember = (tmpMembership.Members || []).find((pMember) => Number(pMember.IDUser) === Number(pIDUser));
		if (!tmpMember) { return; }
		// The direct memberships to delete: the customer-wide grant and/or each directly-held project.
		const tmpTargets = [];
		if (tmpMember.DirectAtUser && Number(this.workspace.idCustomer) > 0)
		{
			tmpTargets.push({ Context: 'Customer', IDContext: Number(this.workspace.idCustomer) });
		}
		if (tmpMember.DirectAtProject)
		{
			(tmpMember.ProjectContextIDs || []).forEach((pID) => { if (Number(pID) > 0) { tmpTargets.push({ Context: 'Project', IDContext: Number(pID) }); } });
		}
		if (!tmpTargets.length)
		{
			tmpMembership.Error = `${tmpMember.Label} inherits this role through another role — remove it from that composing role instead.`;
			this._render();
			return;
		}
		tmpMembership.RemovingID = Number(pIDUser);
		tmpMembership.Error = '';
		this._render();
		let tmpFail = 0;
		for (let i = 0; i < tmpTargets.length; i++)
		{
			const tmpTarget = tmpTargets[i];
			try
			{
				/* eslint-disable-next-line no-await-in-loop */
				await this._deleteJSON(`Permissions/Role/${tmpIDRole}/For/User/${pIDUser}/In/${tmpTarget.Context}/${tmpTarget.IDContext}`);
			}
			catch (pError)
			{
				tmpFail++;
			}
		}
		tmpMembership.RemovingID = 0;
		if (tmpFail)
		{
			tmpMembership.Error = `${tmpFail} membership${tmpFail === 1 ? '' : 's'} could not be removed (you may lack permission in this context).`;
		}
		await this.loadMembersAsync(true);   // reload the members list (re-renders)
	}

	// --- Derive ---

	deriveComposition()
	{
		const tmpComposition = this.composition;
		const tmpEditable = !!this.workspace.isRoleManager;
		tmpComposition.LoadingSlot = tmpComposition.Loading ? [ {} ] : [];
		tmpComposition.ErrorSlot = tmpComposition.Error ? [ { Error: tmpComposition.Error } ] : [];
		tmpComposition.ChildrenSlot = tmpComposition.Children.length ? [ {} ] : [];
		tmpComposition.ChildEmptySlot = (!tmpComposition.Loading && !tmpComposition.Children.length) ? [ {} ] : [];

		// Add-child picker: every role/group except self + already-included children, sorted.
		if (tmpEditable)
		{
			const tmpExclude = new Set([ tmpComposition.SubjectID ]);
			tmpComposition.Children.forEach((pChild) => tmpExclude.add(Number(pChild.IDRole)));
			const tmpCandidates = (this.workspace.rolesAndGroups || [])
				.filter((pRole) => !tmpExclude.has(Number(pRole.IDRole)))
				.map((pRole) => ({ IDRole: pRole.IDRole, Label: `${pRole.Name || `Role ${pRole.IDRole}`}${pRole.Type === 'PermissionGroup' ? ' (group)' : ''}` }))
				.sort((pA, pB) => String(pA.Label).localeCompare(String(pB.Label)));
			tmpComposition.CandidateOptions = [ { IDRole: '', Label: 'Add a role or group…' } ].concat(tmpCandidates);
			tmpComposition.AddSlot = [ {} ];
		}
		else
		{
			tmpComposition.CandidateOptions = [];
			tmpComposition.AddSlot = [];
		}

		// Per-row remove affordance is gated on editability.
		tmpComposition.Children = tmpComposition.Children.map((pChild) =>
			Object.assign({}, pChild, { RemoveSlot: tmpEditable ? [ { IDRoleEdge: pChild.IDRoleEdge } ] : [] }));
	}

	deriveMembership()
	{
		const tmpMembership = this.membership;
		tmpMembership.LoadingSlot = tmpMembership.Loading ? [ {} ] : [];
		tmpMembership.ErrorSlot = tmpMembership.Error ? [ { Error: tmpMembership.Error } ] : [];

		// Cap the RENDERED members. `Permissions/Users/With/Role` is unbounded (a broadly-held base role returns every
		// user — thousands), and the member list renders through a recursive {~TS:~} template that stack-overflows past
		// ~1k rows. Render the first RENDER_CAP into a SEPARATE slot (never mutate the raw Members — deriveMembership runs
		// on every paint), and label the true total honestly. NOTE: the fetch itself is still unbounded; server-side
		// pagination/search on Users/With/Role is the complete fix.
		const tmpAllMembers = Array.isArray(tmpMembership.Members) ? tmpMembership.Members : [];
		const tmpTotal = tmpAllMembers.length;
		const tmpRenderCap = 200;
		tmpMembership.MembersRender = tmpAllMembers.slice(0, tmpRenderCap).map((pMember) =>
		{
			// A remove (×) is offered only to role managers, and only for members who hold the role DIRECTLY
			// at a resolvable context (a customer-wide UserRoleJoin, or one/more project UserContextRoles) —
			// never for a member who only inherits it through a composing role (that has nothing to delete here).
			const tmpRemovable = this.workspace.isRoleManager
				&& (pMember.DirectAtUser || (pMember.DirectAtProject && (pMember.ProjectContextIDs || []).length > 0));
			return Object.assign({}, pMember,
				{
					UserLevelSlot: pMember.AtUserLevel ? [ {} ] : [],
					ProjectLevelSlot: pMember.AtProjectLevel ? [ { CountLabel: this._projectCountLabel(pMember.ProjectCount) } ] : [],
					ViaSlot: pMember.ViaLabel ? [ { ViaLabel: pMember.ViaLabel } ] : [],
					RemoveSlot: tmpRemovable ? [ { IDUser: pMember.IDUser } ] : [],
				});
		});
		tmpMembership.MembersSlot = tmpTotal ? [ {} ] : [];
		tmpMembership.MemberEmptySlot = (!tmpMembership.Loading && !tmpTotal) ? [ {} ] : [];
		tmpMembership.CountLabel = tmpTotal
			? ((tmpTotal > tmpRenderCap) ? `Showing the first ${tmpRenderCap} of ${tmpTotal} members` : `${tmpTotal} member${tmpTotal === 1 ? '' : 's'}`)
			: '';

		// "Add users to this role" panel — shown only to editors (server stays authoritative); its saving/notice/error
		// slots drive the button + messages.
		tmpMembership.AddSlot = this.workspace.isRoleManager ? [ {} ] : [];
		tmpMembership.AddSavingSlot = tmpMembership.AddSaving ? [ {} ] : [];
		tmpMembership.AddErrorSlot = tmpMembership.AddError ? [ { Error: tmpMembership.AddError } ] : [];
		tmpMembership.AddNoticeSlot = tmpMembership.AddNotice ? [ { Notice: tmpMembership.AddNotice } ] : [];
	}

	_render()
	{
		if (this.pict.views.AccessWorkspace)
		{
			this.pict.views.AccessWorkspace.render();
		}
	}
}

module.exports = GroupCompositionProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'GroupComposition',
	AutoInitialize: true,
	AutoInitializeOrdinal: 3,
};
